import {
  BLOCK_MAP,
  getDefaultBlockState
} from "./blockRegistry.ts";
import {
  parseBlockSpec,
  serializeBlockSpec
} from "./blockSpec.ts";

const MAX_PROTOCOL_OPERATIONS = 5000;
const MIN_COORDINATE = -1024;
const MAX_COORDINATE = 1024;
const MIN_HEIGHT = -64;
const MAX_HEIGHT = 320;

function assertNumber(value, fieldName) {
  if (!Number.isFinite(Number(value))) {
    throw new Error("Protocol field " + fieldName + " must be a number.");
  }
  return Number(value);
}

function assertCoordinate(value, fieldName, isHeight) {
  const coordinate = Math.round(assertNumber(value, fieldName));
  const minimum = isHeight ? MIN_HEIGHT : MIN_COORDINATE;
  const maximum = isHeight ? MAX_HEIGHT : MAX_COORDINATE;

  if (coordinate < minimum || coordinate > maximum) {
    throw new Error(
      "Protocol field " + fieldName + " is outside the supported range " +
      minimum + "…" + maximum + "."
    );
  }

  return coordinate;
}

function readPosition(operation, prefix = "") {
  const fieldPrefix = prefix || "";
  return {
    x: assertCoordinate(operation[fieldPrefix + "x"], fieldPrefix + "x", false),
    y: assertCoordinate(operation[fieldPrefix + "y"], fieldPrefix + "y", true),
    z: assertCoordinate(operation[fieldPrefix + "z"], fieldPrefix + "z", false)
  };
}

function validateBlockSpec(
  blockInput,
  fieldName,
  explicitState,
  includeDefaults = true
) {
  if (typeof blockInput !== "string" || blockInput.length === 0) {
    throw new Error("Protocol field " + fieldName + " requires a block id.");
  }

  if (blockInput === "air" || blockInput === "minecraft:air") {
    return null;
  }

  const parsed = parseBlockSpec(blockInput, explicitState);
  if (!parsed || !BLOCK_MAP.has(parsed.blockId)) {
    throw new Error(
      "Unknown block id: " +
      (parsed ? parsed.blockId : blockInput) +
      "."
    );
  }

  return serializeBlockSpec({
    blockId: parsed.blockId,
    state:
      includeDefaults === false
        ? { ...parsed.state }
        : {
            ...getDefaultBlockState(parsed.blockId),
            ...parsed.state
          }
  });
}

function requireBlock(operation) {
  return validateBlockSpec(
    operation.block,
    "block",
    operation.state || {}
  );
}

function rotateFacing(facing, degrees) {
  const directions = ["north", "east", "south", "west"];
  const index = directions.indexOf(facing);
  if (index < 0) {
    return facing;
  }

  const steps = (((degrees / 90) % 4) + 4) % 4;
  return directions[(index + steps) % 4];
}

function transformBlockSpec(blockSpec, transform) {
  const parsed = parseBlockSpec(blockSpec);
  if (!parsed) {
    return null;
  }

  const state = { ...parsed.state };
  const rotation =
    ((Number(transform.rotateY) || 0) % 360 + 360) % 360;

  if (state.facing) {
    state.facing = rotateFacing(state.facing, rotation);
  }

  if (
    state.axis &&
    (rotation === 90 || rotation === 270)
  ) {
    if (state.axis === "x") {
      state.axis = "z";
    } else if (state.axis === "z") {
      state.axis = "x";
    }
  }

  if (
    rotation !== 0 &&
    (
      state.north !== undefined ||
      state.east !== undefined ||
      state.south !== undefined ||
      state.west !== undefined
    )
  ) {
    const directions = ["north", "east", "south", "west"];
    const steps = rotation / 90;
    const previousDirections = {
      north: state.north,
      east: state.east,
      south: state.south,
      west: state.west
    };

    directions.forEach(function (direction) {
      delete state[direction];
    });

    directions.forEach(function (direction, index) {
      const value = previousDirections[direction];
      if (value === undefined) {
        return;
      }
      state[directions[(index + steps) % 4]] = value;
    });
  }

  if (transform.mirrorX) {
    if (state.facing === "east") {
      state.facing = "west";
    } else if (state.facing === "west") {
      state.facing = "east";
    }

    const east = state.east;
    if (east !== undefined || state.west !== undefined) {
      state.east = state.west;
      state.west = east;
    }

    if (state.hinge === "left") {
      state.hinge = "right";
    } else if (state.hinge === "right") {
      state.hinge = "left";
    }

    if (state.shape && state.shape.endsWith("_left")) {
      state.shape = state.shape.replace("_left", "_right");
    } else if (state.shape && state.shape.endsWith("_right")) {
      state.shape = state.shape.replace("_right", "_left");
    }
  }

  if (transform.mirrorZ) {
    if (state.facing === "north") {
      state.facing = "south";
    } else if (state.facing === "south") {
      state.facing = "north";
    }

    const north = state.north;
    if (north !== undefined || state.south !== undefined) {
      state.north = state.south;
      state.south = north;
    }

    if (state.hinge === "left") {
      state.hinge = "right";
    } else if (state.hinge === "right") {
      state.hinge = "left";
    }

    if (state.shape && state.shape.endsWith("_left")) {
      state.shape = state.shape.replace("_left", "_right");
    } else if (state.shape && state.shape.endsWith("_right")) {
      state.shape = state.shape.replace("_right", "_left");
    }
  }

  return serializeBlockSpec({
    blockId: parsed.blockId,
    state
  });
}

function transformPosition(position, transform) {
  let x = position.x;
  let z = position.z;

  if (transform.mirrorX) {
    x = -x;
  }
  if (transform.mirrorZ) {
    z = -z;
  }

  const rotation = ((transform.rotateY || 0) % 360 + 360) % 360;
  if (rotation === 90) {
    const previousX = x;
    x = -z;
    z = previousX;
  } else if (rotation === 180) {
    x = -x;
    z = -z;
  } else if (rotation === 270) {
    const previousX = x;
    x = z;
    z = -previousX;
  } else if (rotation !== 0) {
    throw new Error("rotateY supports only 0, 90, 180, or 270 degrees.");
  }

  return {
    x: x + (transform.offsetX || 0),
    y: position.y + (transform.offsetY || 0),
    z: z + (transform.offsetZ || 0)
  };
}

function mergeTransform(parentTransform, operation) {
  return {
    offsetX: (parentTransform.offsetX || 0) + Number(operation.offsetX || 0),
    offsetY: (parentTransform.offsetY || 0) + Number(operation.offsetY || 0),
    offsetZ: (parentTransform.offsetZ || 0) + Number(operation.offsetZ || 0),
    mirrorX: Boolean(parentTransform.mirrorX) !== Boolean(operation.mirrorX),
    mirrorZ: Boolean(parentTransform.mirrorZ) !== Boolean(operation.mirrorZ),
    rotateY: ((parentTransform.rotateY || 0) + Number(operation.rotateY || 0)) % 360
  };
}

function executeOperation(world, operation, transform) {
  if (!operation || typeof operation !== "object") {
    throw new Error("Protocol operations must be objects.");
  }

  if (operation.op === "group") {
    if (!Array.isArray(operation.ops)) {
      throw new Error("A group operation requires an ops array.");
    }

    const childTransform = mergeTransform(transform, operation);
    let groupChanged = 0;
    operation.ops.forEach(function (childOperation) {
      groupChanged += executeOperation(world, childOperation, childTransform);
    });
    return groupChanged;
  }

  if (operation.op === "set") {
    const point = transformPosition(readPosition(operation), transform);
    let changed = world.setBlock(
      point.x,
      point.y,
      point.z,
      transformBlockSpec(requireBlock(operation), transform)
    ) ? 1 : 0;

    if (operation.blockEntity) {
      changed += world.setBlockEntityData(
        point.x,
        point.y,
        point.z,
        operation.blockEntity
      ) ? 1 : 0;
    }

    return changed;
  }

  if (operation.op === "blockEntity") {
    const point = transformPosition(
      readPosition(operation),
      transform
    );

    if (!world.hasBlock(point.x, point.y, point.z)) {
      throw new Error(
        "blockEntity operation targets an empty position at " +
          point.x + "," + point.y + "," + point.z + "."
      );
    }

    if (!operation.data || typeof operation.data !== "object") {
      throw new Error(
        "blockEntity operation requires a data object."
      );
    }

    return world.setBlockEntityData(
      point.x,
      point.y,
      point.z,
      operation.data
    ) ? 1 : 0;
  }

  if (operation.op === "box" || operation.op === "hollowBox" || operation.op === "line") {
    const first = transformPosition(readPosition(operation, ""), transform);
    const second = transformPosition({
      x: assertCoordinate(operation.x2, "x2", false),
      y: assertCoordinate(operation.y2, "y2", true),
      z: assertCoordinate(operation.z2, "z2", false)
    }, transform);
    const blockSpec = transformBlockSpec(
      requireBlock(operation),
      transform
    );

    if (operation.op === "box") {
      return world.fill(first.x, first.y, first.z, second.x, second.y, second.z, blockSpec);
    }

    if (operation.op === "hollowBox") {
      return world.hollowBox(
        first.x,
        first.y,
        first.z,
        second.x,
        second.y,
        second.z,
        blockSpec,
        Number(operation.thickness || 1)
      );
    }

    return world.line(first.x, first.y, first.z, second.x, second.y, second.z, blockSpec);
  }

  if (operation.op === "cylinder") {
    const center = transformPosition(readPosition(operation), transform);
    return world.cylinder(
      center.x,
      center.y,
      center.z,
      assertCoordinate(operation.radius, "radius", false),
      assertCoordinate(operation.height, "height", true),
      transformBlockSpec(requireBlock(operation), transform),
      Boolean(operation.hollow)
    );
  }

  if (operation.op === "sphere") {
    const center = transformPosition(readPosition(operation), transform);
    return world.sphere(
      center.x,
      center.y,
      center.z,
      assertCoordinate(operation.radius, "radius", false),
      transformBlockSpec(requireBlock(operation), transform),
      Boolean(operation.hollow)
    );
  }

  if (operation.op === "replace") {
    const fromBlockSpec = validateBlockSpec(
      operation.from,
      "from",
      operation.fromState || {},
      false
    );
    const toBlockSpec = validateBlockSpec(
      operation.to,
      "to",
      operation.toState || {}
    );
    return world.replace(fromBlockSpec, toBlockSpec);
  }

  throw new Error("Unsupported protocol operation: " + String(operation.op));
}

export function executeBuildProtocol(world, protocolInput) {
  const protocol = typeof protocolInput === "string"
    ? JSON.parse(protocolInput)
    : protocolInput;

  if (!protocol || !Array.isArray(protocol.ops)) {
    throw new Error("MDP v1 requires an ops array.");
  }

  if (protocol.ops.length > MAX_PROTOCOL_OPERATIONS) {
    throw new Error("Protocol exceeds the operation limit of " + MAX_PROTOCOL_OPERATIONS + ".");
  }

  const rootTransform = {
    offsetX: 0,
    offsetY: 0,
    offsetZ: 0,
    mirrorX: false,
    mirrorZ: false,
    rotateY: 0
  };

  world.beginTransaction(protocol.name || "Run MDP protocol");
  let changed = 0;

  try {
    world.runBatch(function () {
      protocol.ops.forEach(function (operation) {
        changed += executeOperation(world, operation, rootTransform);
      });
    });
    world.commitTransaction();
  } catch (error) {
    world.cancelTransaction();
    throw error;
  }

  return changed;
}

export function createProtocolExample() {
  return {
    format: "minecraft-design-protocol",
    version: 1,
    name: "Stone pavilion example",
    ops: [
      {
        op: "box",
        x: -5,
        y: 0,
        z: -5,
        x2: 5,
        y2: 0,
        z2: 5,
        block: "polished_andesite"
      },
      {
        op: "group",
        offsetY: 1,
        ops: [
          {
            op: "hollowBox",
            x: -4,
            y: 0,
            z: -4,
            x2: 4,
            y2: 5,
            z2: 4,
            block: "stone_bricks"
          },
          {
            op: "box",
            x: -1,
            y: 0,
            z: -4,
            x2: 1,
            y2: 3,
            z2: -4,
            block: "air"
          }
        ]
      },
      {
        op: "cylinder",
        x: 0,
        y: 6,
        z: 0,
        radius: 6,
        height: 2,
        block: "deepslate_tile_slab",
        state: { type: "top", waterlogged: "false" },
        hollow: false
      },
      {
        op: "set",
        x: 0,
        y: 4,
        z: -4,
        block: "lantern",
        state: { hanging: "true", waterlogged: "false" }
      },
      {
        op: "set",
        x: 4,
        y: 1,
        z: 0,
        block: "stone_brick_stairs",
        state: {
          facing: "west",
          half: "bottom",
          shape: "straight",
          waterlogged: "false"
        }
      }
    ]
  };
}
